import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  UserPlus, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Mail, 
  Phone, 
  Calendar,
  Shield,
  ChevronLeft,
  User,
  RefreshCw,
  Eye,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role options for assignment
const ROLE_OPTIONS = [
  { value: "staff", label: "Staff" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
];

const StaffApprovals = ({ user }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' | 'deny'
  const [selectedRole, setSelectedRole] = useState("staff");
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState("pending"); // pending | approved | denied | all

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/staff-applications?status=${filter}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
      } else {
        toast.error("Failed to load applications");
      }
    } catch {
      toast.error("Error loading applications");
    } finally {
      setLoading(false);
    }
  };

  // Fetch applications when filter changes
  useEffect(() => {
    fetchApplications();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetails = (app) => {
    setSelectedApp(app);
    setSelectedRole(app.role_requested || "staff");
    setAdminNotes("");
    setDetailsOpen(true);
  };

  const openConfirmDialog = (action) => {
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const handleAction = async () => {
    if (!selectedApp || !confirmAction) return;

    setProcessing(true);
    try {
      const endpoint = confirmAction === "approve" 
        ? `${API}/admin/staff-applications/${selectedApp.id}/approve`
        : `${API}/admin/staff-applications/${selectedApp.id}/deny`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          role: selectedRole,
          notes: adminNotes,
        }),
      });

      if (response.ok) {
        toast.success(
          confirmAction === "approve" 
            ? "Staff application approved! They can now log in." 
            : "Staff application denied."
        );
        setConfirmOpen(false);
        setDetailsOpen(false);
        fetchApplications();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Action failed");
      }
    } catch {
      toast.error("Failed to process action");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30">Pending</Badge>;
      case "approved":
        return <Badge className="bg-[#22C55E]/20 text-[#22C55E] border-[#22C55E]/30">Approved</Badge>;
      case "denied":
        return <Badge className="bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30">Denied</Badge>;
      default:
        return <Badge className="bg-[#71717A]/20 text-[#71717A]">{status}</Badge>;
    }
  };

  const pendingCount = applications.filter(a => a.status === "pending").length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="staff-approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="text-[#C9C9C9] hover:text-[#D4AF37]"
            data-testid="back-to-dashboard"
          >
            <ChevronLeft size={20} />
            Back
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <Shield className="text-[#D4AF37]" size={28} />
              Staff Approvals
            </h1>
            <p className="text-[#C9C9C9] mt-1">Review and manage staff signup requests</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={fetchApplications}
          className="border-[#27272A] text-[#C9C9C9] hover:text-[#D4AF37] hover:border-[#D4AF37]"
          data-testid="refresh-btn"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards - Cinematic Glass */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${filter === 'pending' ? 'border-[#F59E0B]' : ''}`}
          style={{
            background: filter === 'pending' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(18, 18, 18, 0.7)',
            backdropFilter: 'blur(8px)',
            border: filter === 'pending' ? '1px solid #F59E0B' : '1px solid rgba(39, 39, 42, 0.5)',
          }}
          onClick={() => setFilter('pending')}
          data-testid="filter-pending"
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                <Clock className="text-[#F59E0B]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">Pending</p>
                <p className="text-xl font-semibold text-[#FFFFFF]">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'approved' ? 'border-[#22C55E]' : ''}`}
          style={{
            background: filter === 'approved' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(18, 18, 18, 0.7)',
            backdropFilter: 'blur(8px)',
            border: filter === 'approved' ? '1px solid #22C55E' : '1px solid rgba(39, 39, 42, 0.5)',
          }}
          onClick={() => setFilter('approved')}
          data-testid="filter-approved"
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
                <CheckCircle className="text-[#22C55E]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">Approved</p>
                <p className="text-xl font-semibold text-[#FFFFFF]">
                  {applications.filter(a => a.status === "approved").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'denied' ? 'border-[#EF4444]' : ''}`}
          style={{
            background: filter === 'denied' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(18, 18, 18, 0.7)',
            backdropFilter: 'blur(8px)',
            border: filter === 'denied' ? '1px solid #EF4444' : '1px solid rgba(39, 39, 42, 0.5)',
          }}
          onClick={() => setFilter('denied')}
          data-testid="filter-denied"
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/20 flex items-center justify-center">
                <XCircle className="text-[#EF4444]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">Denied</p>
                <p className="text-xl font-semibold text-[#FFFFFF]">
                  {applications.filter(a => a.status === "denied").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'all' ? 'border-[#D4AF37]' : ''}`}
          style={{
            background: filter === 'all' ? 'rgba(212, 175, 55, 0.1)' : 'rgba(18, 18, 18, 0.7)',
            backdropFilter: 'blur(8px)',
            border: filter === 'all' ? '1px solid #D4AF37' : '1px solid rgba(39, 39, 42, 0.5)',
          }}
          onClick={() => setFilter('all')}
          data-testid="filter-all"
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <UserPlus className="text-[#D4AF37]" size={20} />
              </div>
              <div>
                <p className="text-xs text-[#C9C9C9]">All Applications</p>
                <p className="text-xl font-semibold text-[#FFFFFF]">{applications.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Applications List - Cinematic Glass Card */}
      <Card 
        style={{
          background: 'rgba(18, 18, 18, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)'
        }}
      >
        <CardHeader>
          <CardTitle className="text-lg text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <UserPlus className="text-[#D4AF37]" size={20} />
            {filter === 'all' ? 'All Applications' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Applications`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus size={48} className="text-[#27272A] mx-auto mb-4" />
              <p className="text-[#C9C9C9]">No {filter !== 'all' ? filter : ''} applications found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {applications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-4 rounded-lg transition-all"
                  style={{
                    background: 'rgba(26, 26, 26, 0.6)',
                    border: '1px solid rgba(39, 39, 42, 0.5)',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                    e.currentTarget.style.background = 'rgba(26, 26, 26, 0.8)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(39, 39, 42, 0.5)';
                    e.currentTarget.style.background = 'rgba(26, 26, 26, 0.6)';
                  }}
                  data-testid={`application-${app.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <User className="text-[#D4AF37]" size={24} />
                    </div>
                    <div>
                      <p className="font-medium text-[#FFFFFF]">{app.full_name || app.name}</p>
                      <p className="text-sm text-[#C9C9C9]">{app.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(app.status)}
                        <span className="text-xs text-[#C9C9C9]">
                          Applied: {new Date(app.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDetails(app)}
                    className="border-[#27272A] text-[#C9C9C9] hover:text-[#D4AF37] hover:border-[#D4AF37]"
                    data-testid={`view-${app.id}`}
                  >
                    <Eye size={16} className="mr-2" />
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Application Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-[#141416] border-[#27272A] text-[#FAFAFA] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Shield className="text-[#D4AF37]" size={24} />
              Application Details
            </DialogTitle>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-6 py-4">
              {/* Applicant Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-2xl font-bold text-[#D4AF37]">
                  {(selectedApp.full_name || selectedApp.name || "?")[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-lg font-semibold">{selectedApp.full_name || selectedApp.name}</p>
                  {getStatusBadge(selectedApp.status)}
                </div>
              </div>

              {/* Contact Details */}
              <div className="space-y-3 bg-[#1A1A1D] rounded-lg p-4 border border-[#27272A]">
                <div className="flex items-center gap-3">
                  <Mail className="text-[#71717A]" size={18} />
                  <span className="text-[#A1A1AA]">{selectedApp.email}</span>
                </div>
                {selectedApp.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="text-[#71717A]" size={18} />
                    <span className="text-[#A1A1AA]">{selectedApp.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Calendar className="text-[#71717A]" size={18} />
                  <span className="text-[#A1A1AA]">
                    Applied: {new Date(selectedApp.created_at).toLocaleString()}
                  </span>
                </div>
                {selectedApp.role_requested && (
                  <div className="flex items-center gap-3">
                    <Shield className="text-[#71717A]" size={18} />
                    <span className="text-[#A1A1AA]">
                      Requested Role: {selectedApp.role_requested}
                    </span>
                  </div>
                )}
              </div>

              {/* Approval Actions (only for pending) */}
              {selectedApp.status === "pending" && (
                <div className="space-y-4 bg-[#1A1A1D] rounded-lg p-4 border border-[#27272A]">
                  <div>
                    <label className="text-sm text-[#71717A] block mb-2">Assign Role</label>
                    <Select value={selectedRole} onValueChange={setSelectedRole}>
                      <SelectTrigger className="bg-[#141416] border-[#27272A] text-[#FAFAFA]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141416] border-[#27272A]">
                        {ROLE_OPTIONS.map((role) => (
                          <SelectItem 
                            key={role.value} 
                            value={role.value}
                            className="text-[#FAFAFA] focus:bg-[#D4AF37]/20 focus:text-[#D4AF37]"
                          >
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm text-[#71717A] block mb-2">Admin Notes (optional)</label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes about this decision..."
                      className="bg-[#141416] border-[#27272A] text-[#FAFAFA] resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* History Info (for processed apps) */}
              {selectedApp.status !== "pending" && selectedApp.processed_at && (
                <div className="bg-[#1A1A1D] rounded-lg p-4 border border-[#27272A]">
                  <p className="text-sm text-[#71717A]">
                    {selectedApp.status === "approved" ? "Approved" : "Denied"} on{" "}
                    {new Date(selectedApp.processed_at).toLocaleString()}
                  </p>
                  {selectedApp.processed_by && (
                    <p className="text-sm text-[#71717A]">By: {selectedApp.processed_by}</p>
                  )}
                  {selectedApp.admin_notes && (
                    <p className="text-sm text-[#A1A1AA] mt-2">Notes: {selectedApp.admin_notes}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDetailsOpen(false)}
              className="border-[#27272A] text-[#A1A1AA]"
            >
              Close
            </Button>
            {selectedApp?.status === "pending" && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => openConfirmDialog("deny")}
                  className="bg-[#EF4444] hover:bg-[#DC2626]"
                  data-testid="deny-btn"
                >
                  <XCircle size={16} className="mr-2" />
                  Deny
                </Button>
                <Button
                  onClick={() => openConfirmDialog("approve")}
                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  data-testid="approve-btn"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#141416] border-[#27272A] text-[#FAFAFA] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className={confirmAction === "approve" ? "text-[#22C55E]" : "text-[#EF4444]"} size={24} />
              Confirm {confirmAction === "approve" ? "Approval" : "Denial"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-[#A1A1AA]">
              Are you sure you want to {confirmAction === "approve" ? "approve" : "deny"} the application from{" "}
              <span className="text-[#FAFAFA] font-medium">{selectedApp?.full_name || selectedApp?.name}</span>?
            </p>
            {confirmAction === "approve" && (
              <p className="text-sm text-[#71717A] mt-2">
                They will be assigned the role: <span className="text-[#D4AF37]">{selectedRole}</span> and will be able to log in.
              </p>
            )}
            {confirmAction === "deny" && (
              <p className="text-sm text-[#71717A] mt-2">
                Their account will remain blocked and they won't be able to log in.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              className="border-[#27272A] text-[#A1A1AA]"
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing}
              className={confirmAction === "approve" ? "bg-[#22C55E] hover:bg-[#16A34A]" : "bg-[#EF4444] hover:bg-[#DC2626]"}
              data-testid="confirm-action-btn"
            >
              {processing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : null}
              {confirmAction === "approve" ? "Yes, Approve" : "Yes, Deny"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffApprovals;
